import type { SourceItem } from '@paladigm/answer-visuals';

import type { SystemNoteKind } from '@shared/ai-data-policy/notes';
import type { AiDataPolicyValue } from '@shared/ai-data-policy/types';

// ── AI CIO domain types ─────────────────────────────────────────────────────

import type { ModelName } from '../data/models';
import type { ThinkingChoice } from '../data/thinking-levels';
import type { CompactAnswer, ModelGroup } from '../service/orchestrator-client';

export type MessageRole = 'user' | 'assistant';

/**
 * `queued`: a user message waiting for a turn, shown last in the thread until a turn takes it.
 * `stopped`: a reply the user stopped before it finished.
 */
export type MessageStatus = 'streaming' | 'complete' | 'error' | 'cancelled' | 'queued' | 'stopped';

/** Optional thumbs feedback a user can leave on an assistant message. */
export type MessageFeedback = 'like' | 'dislike' | null;

/**
 * A note on what one of the AI CIO's steps brought back (TASK_COMPLETED's `notes`): its code
 * and the sentence the orchestrator wrote in the session language, such as the CRM's scope
 * (`firm_wide_scope`, `filtered_scope`) or `crm_partial`.
 */
export interface DataNote {
  code: string;
  text: string;
}

/**
 * What one step of a reply brought back (TASK_COMPLETED's `data_state` and `notes`), named as
 * the status line named the step (its TASK_STARTED `label` and `subject`) when it was.
 */
export interface StepHealth {
  step?: string;
  /** `ok`, `empty`, `partial`, `unresolved` or `unavailable`. */
  dataState?: string;
  notes: DataNote[];
}

export interface AttachedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file?: File;
  fileRef?: string;
  vaultFileId?: string;
}

export type AttachmentStatus = 'uploading' | 'ready' | 'failed' | 'unsupported' | 'too-large';

export interface ComposerAttachment {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
  status: AttachmentStatus;
  progress: number;
  fileRef?: string;
  error?: string;
  refusalCode?: string;
  limitBytes?: number;
}

export type PendingResultFileState = 'preparing' | 'failed';

export interface PendingResultFile {
  key: string;
  name: string;
  state: PendingResultFileState;
  outputId?: string;
  taskId?: string;
}

export type MessageEchoKind = 'proposal' | 'interaction';

export interface Message {
  id: string;
  role: MessageRole;
  /**
   * For assistant messages this may contain lightweight markdown/HTML (rendered
   * via RichTextDisplay). For user messages it is plain text.
   */
  content: string;
  createdAt: number;
  status: MessageStatus;
  feedback?: MessageFeedback;
  files?: AttachedFile[];
  /** Rich content blocks (tables/charts/documents) attached to an assistant reply. */
  blocks?: ContentBlock[];
  /** Canonical render order of `blocks` by id (from message_manifest). */
  blockOrder?: string[];
  /**
   * What the reply's steps brought back, from each TASK_COMPLETED that said: the gaps under the
   * answer and the notes on what its figures cover. Absent when no frame carried the fields.
   */
  health?: StepHealth[];
  /** The sources the reply rests on (TASK_COMPLETED's `sources`), each once, as they came. */
  sources?: SourceItem[];
  /**
   * What the AI CIO is doing right now: the orchestrator's latest status line,
   * plain text. Set only while the reply streams; cleared when text arrives
   * after it and when the turn ends.
   */
  statusText?: string;
  /**
   * The turn's model ran at another thinking level than was asked (TURN_MODE with `changed_by`
   * `model`): the reply says so in a short note.
   */
  modeNote?: ModeNote;
  /** What the answer ran with, from its turn's TURN_MODE: its meta line names them. */
  ran?: RanWith;
  /** An open question awaiting the user's answer (renders an MCQ card). */
  pendingInteraction?: InteractionQuestion;
  pendingProposal?: InteractionProposal;
  pendingFiles?: PendingResultFile[];
  echoOf?: MessageEchoKind;
  /**
   * The Connect cards (`connect_account_v1`) that arrived in this reply, in order. Several may,
   * and none is removed: an answered or closed one stays as a record (CC D15).
   */
  connectCards?: ConnectCardRecord[];
  /**
   * Where a queued message is: absent while the orchestrator holds it in the session's queue;
   * `sending` until it answers; `held` when it would not queue it, so it is sent once the
   * running turn ends, or, while a stop that raised no card holds the queue, on "Send now"
   * (`waitsForSendNow`); `stale` when it waits for "Send now": a load found it queued for longer
   * than ten minutes, or a stop that raised no card holds the queue; `refused` when it was not
   * queued (`queueNote` says why).
   */
  queueState?: 'sending' | 'held' | 'stale' | 'refused';
  /** Why a queued message was refused, shown on its row. */
  queueNote?: string;
  /**
   * On a `held` row: a stop that raised no card holds the queue, so it waits for "Send now"
   * too. The next turn ends the hold, and the row goes once that turn ends.
   */
  waitsForSendNow?: boolean;
  /**
   * The session's turn record the row belongs to (`turn_id`): from its stream's SESSION_COMPLETE,
   * on the reply and on the user rows that stream ran, or from the restored thread. Absent on a
   * row no record names, and taken off every row when the chat moves to a fresh session, whose
   * ids start again.
   */
  turnId?: number;
  steps?: ProcessingStep[];
  waiting?: WaitingState;
  route?: MessageRoute;
  workedForMs?: number;
  failure?: ProcessingFailure;
}

/**
 * How full the model's context is (`context` of the session view or of SESSION_COMPLETE, or a
 * compaction's answer): what the next turn's first call carries, of the model's input limit.
 */
export interface ContextFigure {
  usedTokens: number;
  /** The model's input limit; null for a model with no listed limit, which shows no meter. */
  windowTokens: number | null;
  /** An estimate: no turn has been measured on the stored compaction yet. */
  estimated: boolean;
}

/** The token meter's figure: a context with the model's window. */
export interface MeterFigure extends ContextFigure {
  windowTokens: number;
}

/**
 * What this tab's compaction press came to, as the composer's note says it: `compacted` with the
 * first call's tokens before and the last stored fold's after (null when not measured), nothing
 * to compact yet, compaction on Investbanq's own model not available yet, the conversation could
 * not be compacted, compaction unavailable right now, or failed.
 */
export type CompactionOutcome =
  | { kind: 'compacted'; before: number | null; after: number | null }
  | { kind: 'nothing' | 'internalOff' | 'declined' | 'unavailable' | 'failed' };

/**
 * A turn's thinking level as TURN_MODE reports it when the family of the turn's model runs
 * another rank than was asked: each id with the label the frame gives it, for an id the
 * catalog has no name for.
 */
export interface ModeNote {
  /** The level the turn runs at (`thinking_level`, `label`). */
  level: string;
  label: string;
  /** The value the level came from, as sent (`requested_level`, `requested_label`). */
  requested: string;
  requestedLabel: string;
}

/** What an answer ran with, for its meta line. */
export interface RanWith {
  /** The request's model as this client resolves it (shownModel); absent when none is shown. */
  model?: { label: string; group: ModelGroup };
  /** TURN_MODE's level; null on a model that takes no level. */
  level: { id: string; label: string } | null;
}

export interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  /** Id of the model used for this conversation. */
  modelId: string;
  /**
   * The picked model's label and provider, kept with its id, so the chat can name its model once
   * `/models` no longer lists it. Absent for no choice, and on a chat kept before it was stored,
   * which is named by its id.
   */
  modelName?: ModelName;
  /**
   * Orchestrator session bound to this conversation, once created. Maps 1:1 to
   * the CRM backend's stored conversation index (aiCioConversations.sessionId).
   */
  sessionId?: string;
  /**
   * Sessions this conversation was bound to before a send replaced a stale one with
   * a fresh session. A delete erases each of them along with `sessionId`.
   */
  priorSessionIds?: string[];
  /**
   * True once the CRM history index listed `sessionId` or accepted its upsert. Only
   * such a conversation is dropped when the index stops listing it (deleted or
   * cleared elsewhere); one never synced is left alone.
   */
  synced?: boolean;
  /**
   * True when this client created the bound session (a send from this chat). The
   * orchestrator keeps the last model a request named on the session and never
   * reports it, so only then does the "no choice" sentinel mean its default. A
   * chat restored from the history index may run a model picked elsewhere.
   */
  sessionStartedHere?: boolean;
  /**
   * The session read a connected account's content (SESSION_CONNECTED, or a session read with
   * `connected`): every model call runs the deployment's model from then on, so the control
   * hides its model panes and draws that model's levels.
   */
  connected?: boolean;
  /**
   * The chat's thinking choice: the level's id and rank, or null while none was chosen (each
   * model then shows its own default). A new chat starts from the user's last choice
   * (useThinkingLevelStore); absent on a chat kept before levels were per chat, which reads the
   * user's last choice until its first write (chatChoice).
   */
  thinking?: ThinkingChoice | null;
  /** The chat's Agentic switch: off (absent) in every new chat, kept over a model change. */
  agentic?: boolean;
  systemNotes?: ChatSystemNote[];
  investorData?: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface AiModel {
  id: string;
  /** Display label exactly as shown in the model selector (e.g. "Opus 4.7"). */
  label: string;
  /** Short description shown under the label in the selector. */
  description: string;
  /** Backend provider that serves this model (openai / anthropic / gemini). */
  provider?: string;
}

// ── Rich content blocks (from the orchestrator) ─────────────────────────────
//
// The orchestrator streams structured blocks (tables, charts, documents, …)
// alongside plain text. We keep the envelope close to the wire format so the
// renderer can grow without reshaping. See FRONTEND_INTEGRATION.md §7.

export type ContentBlockType =
  | 'text'
  | 'metric_card'
  | 'metric_group'
  | 'table'
  | 'chart'
  | 'image'
  | 'file'
  | 'document'
  | 'list'
  | 'code'
  | 'agent_card'
  | 'error_block'
  | 'visual';

export interface ContentBlock {
  id: string;
  type: ContentBlockType;
  metadata?: Record<string, unknown>;
  payload?: Record<string, unknown>;
  taskId?: string;
  outputId?: string;
  vaultFileId?: string;
}

// ── Interactions (MCQ / clarifying questions) ───────────────────────────────
//
// The orchestrator can pause a turn to ask a structured question. The answer
// is posted back to /messages with an `interaction` body keyed by the
// question's intent_id/flow/question_id. See core/src main.py
// InteractionDecisionPayload.

export interface InteractionOption {
  id: string;
  label: string;
  /** True for the "Other (type a value)" escape option → show a text input. */
  isFreeForm?: boolean;
}

export interface InteractionQuestion {
  intentId: string;
  flow: string;
  questionId: string;
  prompt: string;
  options: InteractionOption[];
}

/** A user's answer to an open {@link InteractionQuestion}. */
export interface InteractionAnswer {
  question: InteractionQuestion;
  /** Chosen option id (or the free-form option id). */
  selection: string;
  /** Typed value when the chosen option is free-form. */
  text?: string;
  /**
   * The chat's thinking choice and Agentic switch when the user answered. A stop card's
   * "Something else" runs with them, also when it is sent only once a running turn ends;
   * every other answer runs in its card's mode. Absent → the chat's as it is sent.
   */
  controls?: ThinkingControls;
}

/** The chat's thinking choice and Agentic switch (Conversation.thinking, Conversation.agentic). */
export interface ThinkingControls {
  choice: ThinkingChoice | null;
  agentic: boolean;
}

export interface InteractionProposal {
  intentId: string;
  flow: string;
  proposalId: string;
  title: string;
  schema: string;
  content: Record<string, unknown>;
  rationale: string;
  tradeoffs: string[];
  plainText: string;
}

export type ProposalDecisionKind = 'approve' | 'restart';

/** The Connect card's schema: a search's consent card for a service the user has not connected. */
export const CONNECT_CARD_SCHEMA = 'connect_account_v1';

/** A Connect the card's own click started: its service and when (CC D15, D16). */
export interface ConnectPending {
  service: string;
  /** `Date.now()` at the click. */
  startedAt: number;
  /**
   * The Connect's start on the CRM's clock: the `statusChangedAt` of the service's `CONNECTING`
   * read once `connectIntent` answered (the attempt's start), in ms. Absent until that read.
   */
  serverStartedAt?: number;
}

/**
 * How the card's last Connect of a service went, when it did not connect: Denied (`denied`,
 * `expired`, `cancelled` or an explicit Cancel), No answer (the tab read closed with no result),
 * a blocked pop-up, or an Error (`code`: the result, the CRM's reason or a `connectIntent` code).
 */
export interface ConnectCardOutcome {
  service: string;
  kind: 'denied' | 'no_answer' | 'popup_blocked' | 'error';
  code?: string;
}

/** The badge a Connect card shows (DEV-4193), kept as its record's last. */
export type ConnectBadge =
  | 'not_connected'
  | 'connecting'
  | 'connected'
  | 'failed'
  | 'reconnect'
  | 'unavailable';

/**
 * One Connect card, persisted with the message it arrived in (CC D15). The card's pending Connect
 * and its last outcome live here, never in the component, so a conversation switch, a closed panel
 * or a reload loses neither.
 */
export interface ConnectCardRecord {
  proposal: InteractionProposal;
  /** The conversation's model when the card arrived: its answer sends it. */
  modelId: string;
  /**
   * `Date.now()` when the card arrived. A Reconnect's service that reads connected since then was
   * connected elsewhere; before then, it is the lapsed connection (CC D15).
   */
  arrivedAt: number;
  /** A Connect the card's own click started and that has not ended. */
  pending?: ConnectPending;
  outcome?: ConnectCardOutcome;
  /** The last badge it showed. */
  badge?: ConnectBadge;
  /** Answered, closed, or moved on from: it shows its last badge, answers nothing, reads nothing. */
  record?: boolean;
}

export interface ProposalDecision {
  proposal: InteractionProposal;
  decision: ProposalDecisionKind;
}

export interface PortfolioPlanRow {
  asset_class: string;
  share: number;
  amount: number;
  country_id?: string | null;
  industry_id?: string | null;
}

export interface PortfolioPlanContent {
  theme_id?: string | null;
  goal_summary?: string;
  capital: number;
  risk_level: number;
  horizon_years: number;
  allocation: PortfolioPlanRow[];
  notes?: string[];
}

export interface RememberPreferenceContent {
  summary: string;
  kind?: string;
  dimension?: string | null;
  value?: unknown;
}

// ── Service seam ─────────────────────────────────────────────────────────────

export interface AttachedRef {
  attachmentId: string;
  fileRef: string;
  vaultFileId?: string;
}

export type VaultSave =
  | { kind: 'upload'; fileRef: string; vaultFileId: string }
  | { kind: 'output'; vaultFileId: string; outputId?: string; blockId?: string; name?: string };

export interface PreparingOutput {
  taskId: string;
  name: string;
  outputId?: string;
}

export interface OutputFailure {
  name: string;
  taskId?: string;
  outputId?: string;
}

/** A chunk emitted while an assistant reply streams in. */
export interface StreamChunk {
  /** Text appended to the reply so far (delta, not the full content). */
  delta: string;
  done: boolean;
  /**
   * Session id assigned by the backend for this conversation. Emitted once,
   * early, so the store can persist the orchestrator session ↔ conversation map.
   */
  sessionId?: string;
  /** A finished rich content block (table/chart/document/…), if any. */
  block?: ContentBlock;
  /** Canonical block render order (from message_manifest), if any. */
  blockOrder?: string[];
  /** What a finished step brought back, when its TASK_COMPLETED said (`data_state`, `notes`). */
  health?: StepHealth;
  /** The sources a finished step read (TASK_COMPLETED's `sources`), when it named any. */
  sources?: SourceItem[];
  /** The orchestrator's latest status line (its STATUS frame), plain text. */
  status?: string;
  /**
   * The turn runs at another thinking level than was asked because its model's family has
   * none at the rank asked (TURN_MODE with `changed_by: "model"`). Read only when the request
   * was built from a `/models` that carried `thinking_scale`; on a card answer, only on a stop
   * card's "Something else".
   */
  modeNote?: ModeNote;
  /**
   * What the turn runs with (TURN_MODE): its level, and the request's model. Read under the
   * same rule as `modeNote`.
   */
  ran?: RanWith;
  /** The session became a connected session (SESSION_CONNECTED). */
  connected?: boolean;
  investorData?: boolean;
  /** A structured question the orchestrator is asking (pauses the turn). */
  interaction?: InteractionQuestion;
  proposal?: InteractionProposal;
  /** Set on the terminal chunk when the reply failed. */
  error?: boolean;
  filesRefused?: boolean;
  attached?: AttachedRef[];
  vaultSaves?: VaultSave[];
  preparing?: PreparingOutput[];
  outputFailures?: OutputFailure[];
  /**
   * Queued messages the turn took (MESSAGE_TAKEN), shown as the user's from here on: at the
   * turn's opening before the request's own message, or mid-turn (`intake`) after the reply so
   * far, which then goes on in a new reply.
   */
  taken?: { id: string; text: string }[];
  intake?: boolean;
  /**
   * The user stopped the turn (TURN_STOPPED); its stop card follows, or, when nothing had
   * finished, one fixed line that says so, as the stopped reply's text.
   */
  stopped?: boolean;
  /** The card the turn consumed or closed (interaction_resolved), by its id. */
  resolved?: string;
  /** On the terminal chunk: the session's queued messages as the turn ended, when known. */
  queued?: number;
  /**
   * On the terminal chunk: the queue is held. After a stop, until the stop card is answered
   * or, when it raised none, until the user's next message or "Send now"; after a typed
   * phrase's mode card, until the card's answer or a later message, with no "Send now".
   */
  queueHeld?: boolean;
  /** Terminal: the request ran no turn, and this is how the orchestrator answered instead. */
  answer?: TurnAnswer;
  /** On the terminal chunk: the record the request's turn ended with (`turn_id`). */
  turnId?: number;
  /** On the terminal chunk: the session's context as the turn ended; absent when not said. */
  context?: ContextFigure | null;
  /** On the terminal chunk: the last turn the stored compaction folded; absent when not said. */
  compactedThroughTurn?: number | null;
  step?: ProcessingStep;
  waiting?: WaitingState | null;
  route?: MessageRoute;
  workedForMs?: number;
  failure?: ProcessingFailure;
}

/**
 * How the orchestrator answered a request that ran no turn: the message was `queued` behind
 * the running turn, or `refused` by the guardrail as it arrived (nothing kept); the queue was
 * `full`; a turn or an operation holds the session and the request was not taken (`busy`); a
 * drain found a turn `running`, or nothing queued (`empty`); a Connect card's answer found the
 * card already answered or closed (`closed`: any other 409, read only on that answer).
 */
export type TurnAnswer =
  | { status: 'queued' }
  | { status: 'refused'; message: string }
  | { status: 'full' }
  | { status: 'busy' }
  | { status: 'running' }
  | { status: 'empty' }
  | { status: 'closed' };

/** What a conversation's turn is doing, as this tab knows it. */
export interface TurnActivity {
  /**
   * A reply is being written, or a compaction holds the session: a send is queued, and Stop
   * replaces Send unless it is a compaction.
   */
  busy: boolean;
  /** Stop was pressed and the reply has not ended. */
  stopping: boolean;
  /** The reply is one this tab does not read (another tab's, or one a reload found running). */
  following: boolean;
  /**
   * A compaction holds the session: this tab's press (its calls and its waits), or one the last
   * session read showed. The composer is held, with no Stop, and the thread shows no "Still
   * answering".
   */
  compacting: boolean;
  /** The newest context the chat holds; null while it holds none (no meter). */
  context: ContextFigure | null;
  /** The newest cut: the last turn the stored compaction folded, null for none. */
  compactedThroughTurn: number | null;
  /** What this tab's last press came to, until Dismiss, the chat's next Send or a lowering pick. */
  compactionNote: CompactionOutcome | null;
  /**
   * The chat's model a press answered that compaction on Investbanq's own model is not available
   * yet for: the control is held while the chat keeps that model.
   */
  compactOffFor: string | null;
  /** A press answered that compaction is not offered on the session (its platform, connected). */
  compactHidden: boolean;
  /** A press found the session gone: the meter, its control and the divider hide. */
  sessionGone: boolean;
}

/** The live session as `GET /sessions/{id}` returns it. */
export interface LiveSession {
  turns: TranscriptTurn[];
  /** A turn holds the session. */
  turnRunning: boolean;
  /** The messages waiting for a turn, oldest first. */
  queue: { id: string; text: string; createdAt: number }[];
  /** The open stop card. */
  stopCard?: InteractionQuestion;
  /**
   * The queue is held: the stop card or a typed phrase's mode card is open, or the last turn
   * was a stop that raised none. Absent when the orchestrator does not say, which reads as not
   * held.
   */
  queueHeld?: boolean;
  /**
   * The hold is a typed phrase's mode card's: the orchestrator runs the queue after the card's
   * answer, so the rows stay queued, with no "Send now", and nothing drains.
   */
  modeCardOpen?: boolean;
  /** The session is a connected session. */
  connected?: boolean;
  /** A compaction holds the session (absent while none does, and from an older orchestrator). */
  compacting?: boolean;
  /** The last turn the stored compaction folded, null for none; absent from an older one. */
  compactedThroughTurn?: number | null;
  /** The session's context, null while no turn was measured; absent from an older one. */
  context?: ContextFigure | null;
}

export interface SendOptions {
  /** Full prior history for context (most recent last). */
  history: Message[];
  modelId: string;
  /**
   * The conversation's session may run a model picked elsewhere (see isSessionModelUnknown),
   * so the control shows no model and offers the scale's Low, Medium and High.
   */
  sessionModelUnknown?: boolean;
  /** The conversation's session is a connected session: it runs the deployment's model. */
  connected?: boolean;
  /** The chat's thinking choice (chatChoice): the level the request carries. */
  thinking?: ThinkingChoice | null;
  /** The chat's Agentic switch; absent reads as off. */
  agentic?: boolean;
  /**
   * Existing orchestrator session for this conversation, if one was already
   * created. When absent, the service creates a new session and reports its id
   * back via the first {@link StreamChunk.sessionId}.
   */
  sessionId?: string;
  files?: AttachedFile[];
  /** Aborts an in-flight reply. */
  signal?: AbortSignal;
  /**
   * The id the message is queued under when a turn runs in the session (its
   * `client_message_id`). A message sent without one is never queued (a regenerate).
   */
  clientMessageId?: string;
  forceInternal?: boolean;
  queued?: boolean;
  investorData?: boolean;
  blockedToolsets?: string[];
}

export interface UploadOptions {
  signal?: AbortSignal;
  onProgress?: (percent: number) => void;
}

export type UploadRefusalCode = 'type_not_allowed' | 'too_large' | 'empty_file';

export interface UploadRefusal {
  status: number;
  message: string;
  code?: string;
  limitBytes?: number;
}

export interface AttachmentPolicy {
  accept: string;
  extensions: readonly string[];
  maxBytes: number;
  maxFiles: number;
  acceptsAudio: boolean;
}

/**
 * The single seam between the AI CIO UI and whatever produces replies.
 *
 * The app talks ONLY to this interface. It is backed either by a local mock
 * ({@link createMockChatService}) or by the real orchestrator client
 * ({@link createOrchestratorChatService}); the active one is chosen via
 * `setChatService` with zero changes to the UI or store.
 */
export interface ChatService {
  /**
   * Streams an assistant reply for `prompt`. Implementations yield text deltas
   * (and optional blocks / sessionId / interaction question) and a final
   * `done: true` chunk. Should respect `options.signal`.
   */
  streamReply(prompt: string, options: SendOptions): AsyncIterable<StreamChunk>;

  uploadFile?(file: File, options?: UploadOptions): Promise<{ fileRef: string }>;

  /**
   * Answers an open interaction question (MCQ) and streams the continued turn.
   * Optional — the mock omits it. `options.sessionId` is required here.
   */
  answerInteraction?(answer: InteractionAnswer, options: SendOptions): AsyncIterable<StreamChunk>;

  decideProposal?(decision: ProposalDecision, options: SendOptions): AsyncIterable<StreamChunk>;

  /** Transcribe recorded audio to text (STT). Optional — mock omits it. */
  transcribe?(audio: Blob, language?: string, signal?: AbortSignal): Promise<string>;

  /**
   * Restores a server session's transcript (role + markdown text per turn) so a
   * conversation known only from the history index can be reopened. Optional — mock omits it.
   */
  loadTranscript?(sessionId: string, signal?: AbortSignal): Promise<TranscriptTurn[]>;

  attachmentPolicy?: AttachmentPolicy;
  deleteFile?(fileRef: string): Promise<void>;

  /**
   * Reads the live session: its history, whether a turn runs, its queue and the open stop
   * card. Null once the session is gone. Optional — mock omits it.
   */
  readSession?(sessionId: string, signal?: AbortSignal): Promise<LiveSession | null>;

  /**
   * Runs what is queued in the session as the next turn (a drain). Optional — mock omits it.
   * `options.sessionId` is required here.
   */
  drainQueue?(options: SendOptions): AsyncIterable<StreamChunk>;

  /**
   * Asks the session's running turn to stop at its next safe point: true when one was
   * running. Its reply goes on to the stop card. Optional — mock omits it.
   */
  stopTurn?(sessionId: string): Promise<boolean>;

  /**
   * Removes a queued message: true when it was removed, false once a turn has taken it.
   * Optional — mock omits it.
   */
  removeQueued?(sessionId: string, messageId: string): Promise<boolean>;

  /**
   * Folds the session's older turns into a summary the model reads in their place, with the
   * model the chat's next message would send (`modelId` as the chat keeps it). Optional — mock
   * omits it.
   */
  compactSession?(sessionId: string, modelId: string, signal?: AbortSignal): Promise<CompactAnswer>;

  /**
   * Erases a session on the server: its saved transcript, the memory recalled from
   * it and the live session. Idempotent.
   */
  deleteSession(sessionId: string): Promise<DeleteOutcome>;

  /**
   * Erases every saved conversation of the user on the server. Idempotent. `running` names
   * the sessions this tab knows a turn runs in: they are stopped before a refused clear is
   * tried again.
   */
  clearHistory(running?: string[]): Promise<DeleteOutcome>;
}

/**
 * How a server-side delete or clear ended: done; refused because a reply is still
 * being written in the conversation; or failed (try again).
 */
export type DeleteOutcome = 'deleted' | 'busy' | 'failed';

export interface TranscriptTurn {
  role: 'user' | 'assistant';
  content: string;
  files?: AttachedFile[];
  blocks?: ContentBlock[];
  blockOrder?: string[];
  /** A reply the user stopped before it finished. */
  stopped?: boolean;
  /** Its record's turn id (`turn_id` of the live session, `turn_index` of the archive). */
  turnId?: number;
}

export type ProcessingStepStatus = 'active' | 'done' | 'failed' | 'skipped' | 'stopped';

export interface ProcessingStep {
  id: string;
  label: string;
  detail?: string;
  status: ProcessingStepStatus;
  durationMs?: number;
  kind?: 'route' | 'generic';
}

export interface WaitingState {
  kind: 'data_policy';
}

export type ProcessingFailureCode = 'E6' | 'E9' | 'E10' | 'E10b' | 'generic';

export interface ProcessingFailure {
  code: ProcessingFailureCode;
  offerInternalFallback?: boolean;
}

export type DataCategory = 'investor' | 'crm';

export type RouteReasonCode = 'R1' | 'R1b' | 'R1c' | 'R1d' | 'R2' | 'R3' | 'R4' | 'R5' | 'FB';

export interface RouteModel {
  id: string;
  label: string;
  group: ModelGroup;
  providerLabel: string;
  kind: 'internal' | 'commercial';
}

export type SharedCategory = 'question' | 'chat' | 'investorData' | 'fileText';

export interface MessageRoute {
  modelUsed: RouteModel;
  selectedModel: RouteModel;
  reasonCode: RouteReasonCode;
  categories: DataCategory[];
  policy: { value: AiDataPolicyValue; setByName: string | null; setAt: string | null };
  sharedWithProvider: string | null;
  sharedCategories: SharedCategory[];
  dataUsed: string | null;
  dataUsedKind: DataCategory | null;
}

export interface ChatSystemNote {
  id: string;
  afterMessageId: string | null;
  kind: SystemNoteKind;
  actorName: string | null;
  at: string | null;
  modelLabel: string | null;
  createdAt: number;
}
